import { AlertCircle, ArrowLeftRight, FileSpreadsheet, ShieldCheck, Upload } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";

import { CONCEPT_MAX_LENGTH } from "../../constants/payoutBatches.constants";
import { useBatchErrorMessage } from "../../hooks/useBatchErrorMessage";
import { useBatchSetupOptions } from "../../hooks/useBatchSetupOptions";
import { useTemplateDownload } from "../../hooks/useTemplateDownload";
import type { BatchError } from "../../utils/batchErrors";
import { type BatchSetupValues, buildBatchSetupSchema, splitCorridor } from "../../utils/batchSetupSchema";
import { BatchFileDropzone } from "./BatchFileDropzone";
import { BatchRouteFields } from "./BatchRouteFields";
import { OrganizationField } from "./OrganizationField";
import { SetupStep } from "./SetupStep";
import { TemplateDownloadCard } from "./TemplateDownloadCard";

export interface BatchSetupFormProps {
  isInternal: boolean;
  onSubmit: (values: BatchSetupValues) => void | Promise<void>;
  isSubmitting: boolean;
  submitError: BatchError | null;
}

/**
 * Step 1 of the wizard, in the order the work happens: where the money comes from and where it goes, the
 * template for that corridor, then the filled file. Staff first say which organization they act for; nothing
 * else loads until then.
 */
export function BatchSetupForm({ isInternal, onSubmit, isSubmitting, submitError }: BatchSetupFormProps) {
  const { t } = useTranslations("payoutBatches");
  const describeError = useBatchErrorMessage();

  const schema = useMemo(
    () =>
      buildBatchSetupSchema({
        requireOrganization: isInternal,
        messages: {
          organizationRequired: t("org_picker_required_hint"),
          walletRequired: t("setup_wallet_required"),
          corridorRequired: t("setup_corridor_required"),
          fileRequired: t("file_error_required"),
        },
      }),
    [isInternal, t],
  );
  const form = useZodForm(schema, {
    organizationId: "",
    sourceCurrency: "",
    corridor: "",
    concept: "",
  });

  const organizationId = (form.watch("organizationId") as string | undefined) || null;
  const sourceCurrency = form.watch("sourceCurrency");
  const corridorRaw = form.watch("corridor");
  const file = form.watch("file");

  const options = useBatchSetupOptions(isInternal ? organizationId : null);
  const template = useTemplateDownload();

  const corridor = corridorRaw ? splitCorridor(corridorRaw) : null;
  const locked = !options.canLoad;

  return (
    <ColocaForm form={form} onSubmit={onSubmit}>
      <ColocaSurface padding="lg" className="space-y-8">
        {isInternal && (
          <div className="border-b border-border pb-6">
            <OrganizationField />
          </div>
        )}

        <div>
          <SetupStep
            icon={ArrowLeftRight}
            title={t("setup_route_title")}
            done={!!sourceCurrency && !!corridor}
          >
            <BatchRouteFields
              wallets={options.wallets}
              walletsLoading={options.walletsLoading}
              walletsError={options.walletsError}
              corridorOptions={options.corridorOptions}
              locked={locked}
              disabled={isSubmitting}
            />
          </SetupStep>

          <SetupStep
            icon={FileSpreadsheet}
            title={t("setup_template_title")}
            done={!!corridorRaw && template.downloaded === corridorRaw}
          >
            <TemplateDownloadCard
              corridor={corridor}
              onDownload={(country, rail) => void template.download(country, rail)}
              isDownloading={template.isDownloading}
              errorMessage={template.error ? describeError(template.error) : null}
            />
          </SetupStep>

          <SetupStep icon={Upload} title={t("setup_file_title")} done={file instanceof File} last>
            <BatchFileDropzone disabled={locked || isSubmitting} />
            <ColocaInputField
              name="concept"
              label={t("setup_concept_label")}
              description={t("setup_concept_description")}
              placeholder={t("setup_concept_placeholder")}
              maxLength={CONCEPT_MAX_LENGTH}
              disabled={locked || isSubmitting}
            />
          </SetupStep>
        </div>

        {submitError && (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            heading={t("setup_upload_error_title")}
            text={describeError(submitError)}
          />
        )}

        <div className="flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-end">
          <p className="text-xs text-muted-foreground">{t("setup_submit_hint")}</p>
          <ColocaButton
            type="submit"
            variant="primary"
            icon={ShieldCheck}
            loading={isSubmitting}
            disabled={locked || isSubmitting}
          >
            {isSubmitting ? t("setup_submitting") : t("setup_submit")}
          </ColocaButton>
        </div>
      </ColocaSurface>
    </ColocaForm>
  );
}
